import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { formatCurrencyWhole } from '../../../utils/formatCurrency.js';
import CollapsiblePanel from './CollapsiblePanel.js';

const VISIBLE_ROWS = 7;
const ROW_HEIGHT_PX = 40;

const COLUMNS = [
  {
    key: 'undispatched',
    titleKey: 'dashboard.undispatched',
    locationKey: 'dashboard.origin',
    locationDefault: 'Origin',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'in_transit',
    titleKey: 'dashboard.inTransit',
    locationKey: 'dashboard.destination',
    locationDefault: 'Destination',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'pending_delivery',
    titleKey: 'dashboard.pendingDelivery',
    locationKey: 'dashboard.destination',
    locationDefault: 'Destination',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'pending_dispatch',
    titleKey: 'dashboard.pendingDispatch',
    locationKey: 'dashboard.origin',
    locationDefault: 'Origin',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
  {
    key: 'cargo_missing',
    titleKey: 'dashboard.cargoMissing',
    locationKey: 'dashboard.destination',
    locationDefault: 'Destination',
    headerClass: 'bg-gray-50 text-gray-600',
    bodyClass: 'bg-white-50/70',
    accent: 'border-gray-200',
  },
];

const CargoColumn = ({ column, data, onOpen }) => {
  const { t } = useTranslation();
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const isMissing = column.key === 'cargo_missing';
  const colWidths = isMissing
    ? ['30%', '16%', '16%', '16%', '22%']
    : ['36%', '20%', '18%', '26%'];
  const cell = 'px-2 sm:px-3 py-2 text-sm text-gray-800';
  const head = 'px-2 sm:px-3 py-2 text-sm font-semibold text-gray-700';

  return (
    <div className="w-full min-w-0 border border-gray-200 rounded-xl overflow-hidden sm:w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-1.5rem)/3)]">
      <button
        type="button"
        onClick={() => onOpen(column.key, null)}
        className={`w-full px-3 py-2.5 text-center text-base font-semibold ${column.headerClass}`}
      >
        {t(column.titleKey)}
      </button>
      <div className="overflow-y-auto" style={{ maxHeight: (VISIBLE_ROWS + 1) * ROW_HEIGHT_PX }}>
        <table className="w-full table-fixed">
          <colgroup>
            {colWidths.map((width, index) => <col key={`col-${index}`} style={{ width }} />)}
          </colgroup>
          <thead className="sticky top-0 bg-white text-gray-700">
            <tr>
              <th className={`${head} text-left`}>{t(column.locationKey, { defaultValue: column.locationDefault })}</th>
              {isMissing ? (
                <>
                  <th className={`${head} text-center`}>{t('dashboard.transit')}</th>
                  <th className={`${head} text-center`}>{t('dashboard.delivery')}</th>
                </>
              ) : (
                <th className={`${head} text-center`}>Shipment</th>
              )}
              <th className={`${head} text-center`}>{t('dashboard.qty')}</th>
              <th className={`${head} text-center`}>{t('dashboard.totalAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${column.key}-${row.hub_id || 'none'}`} className="border-t border-gray-100">
                <td className={`${cell} text-left`}>
                  <button
                    type="button"
                    onClick={() => onOpen(column.key, row.hub_id)}
                    className="text-blue-600 hover:underline text-left break-words leading-tight"
                  >
                    {row.hub_name}
                    {row.is_hub ? (
                      <span className="hidden sm:inline ml-1 text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500">Hub</span>
                    ) : null}
                  </button>
                </td>
                {isMissing ? (
                  <>
                    <td className={`${cell} text-center`}>{row.transit || 0}</td>
                    <td className={`${cell} text-center`}>{row.delivery || 0}</td>
                  </>
                ) : (
                  <td className={`${cell} text-center`}>{row.gc_count || 0}</td>
                )}
                <td className={`${cell} text-center`}>{row.qty || 0}</td>
                <td className={`${cell} text-center tabular-nums`}>{formatCurrencyWhole(row.amount)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={isMissing ? 5 : 4} className="px-3 py-6 text-center text-sm text-gray-400">
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-3 border-t border-gray-200 bg-gray-50/70 text-sm font-medium text-gray-700">
        <div className="px-2 py-2 text-center border-r border-gray-200">
          Shipment: {isMissing
            ? Number(totals.gc_count || 0) || (Number(totals.transit || 0) + Number(totals.delivery || 0))
            : (totals.gc_count || 0)}
        </div>
        <div className="px-2 py-2 text-center border-r border-gray-200">
          {t('dashboard.qty')}: {totals.qty || 0}
        </div>
        <div className="px-2 py-2 text-center tabular-nums">
          {formatCurrencyWhole(totals.amount)}
        </div>
      </div>
    </div>
  );
};

const PRIMARY_COLUMN_COUNT = 3;

const CargoOverviewSection = ({
  data,
  onOpenBucket,
  hubFilter = null,
}) => {
  const { t } = useTranslation();
  const asOn = data?.as_on;
  const [showRest, setShowRest] = useState(false);
  const primaryColumns = COLUMNS.slice(0, PRIMARY_COLUMN_COUNT);
  const restColumns = COLUMNS.slice(PRIMARY_COLUMN_COUNT);

  const renderColumns = (columns) => (
    <div className="flex flex-wrap gap-3">
      {columns.map((column) => (
        <CargoColumn
          key={column.key}
          column={column}
          data={data?.[column.key]}
          onOpen={onOpenBucket}
        />
      ))}
    </div>
  );

  return (
    <CollapsiblePanel
      title={t('dashboard.overviewCargoCount')}
      defaultOpen
      trailing={(
        <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full">
          {hubFilter}
          {asOn ? <span className="text-[11px] sm:text-xs text-gray-400 whitespace-nowrap">As on {asOn}</span> : null}
        </div>
      )}
    >
      {renderColumns(primaryColumns)}
      {restColumns.length > 0 && (
        <>
          <div className="flex justify-center py-2">
            <button
              type="button"
              onClick={() => setShowRest((open) => !open)}
              aria-expanded={showRest}
              aria-label={showRest
                ? t('dashboard.hideRemainingTables', { defaultValue: 'Hide remaining tables' })
                : t('dashboard.showRemainingTables', { defaultValue: 'Show remaining tables' })}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
            >
              <ChevronDown className={`h-5 w-5 transition-transform ${showRest ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {showRest ? renderColumns(restColumns) : null}
        </>
      )}
    </CollapsiblePanel>
  );
};

export default CargoOverviewSection;
